<template>
  <div v-if="isInitialized" class="card">
    <div class="card-body">
      <div class="soft-gradient p-3 rounded d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-2 rounded-2 me-2">
            <vue-feather type="edit-3" stroke="#652aa3" size="20"></vue-feather>
          </div>
          <h5 class="mb-0">Self Review</h5>
        </div>
        <div v-if="review?.status">
          <span :class="getStatusBadgeClass(review.status)" class="p-2">
            {{ formatStatus(review.status) }}
          </span>
        </div>
      </div>

      <div v-if="!review || (review.status !== 'self_review' && !isCompleted)" class="alert alert-info">
        <i class="icon-feather-info me-2"></i>
        {{ getStatusMessage() }}
      </div>

      <!-- Self Review Form -->
      <form v-else @submit.prevent="submitReview">
        <!-- Review Sections -->
        <div v-if="templateSections.length === 0" class="alert alert-warning">
          <i class="icon-feather-alert-triangle me-2"></i>
          No review sections found. Please contact your administrator.
        </div>

        <template v-else>
          <div v-for="(section, index) in templateSections" :key="index" class="mb-4 p-3 border rounded">
            <h6 class="text-primary mb-3">{{ section.title }}</h6>
            <p v-if="section.description" class="text-muted small mb-3">{{ section.description }}</p>

            <!-- Criteria Questions -->
            <div v-for="criterion in getCriteriaForSection(section.title)" :key="criterion.id" class="mb-4 p-3 border rounded bg-light">
              <h6 class="fw-semibold">{{ criterion.question }}</h6>
              <p v-if="criterion.description" class="text-muted small mb-3">{{ criterion.description }}</p>

              <!-- Response based on answer type -->
              <div class="mb-3">
                <label class="form-label">Your Response</label>

                <!-- Text Response -->
                <textarea
                  v-if="criterion.answer_type === 'text'"
                  v-model="criteriaResponses[criterion.id].response"
                  class="form-control"
                  rows="3"
                  :placeholder="`Enter your response for: ${criterion.question}`"
                  :required="!criterion.is_optional"
                ></textarea>

                <!-- Rating Input -->
                <template v-else-if="criterion.answer_type === 'rating'">
                  <div class="d-flex align-items-center mb-2">
                    <input
                      type="range"
                      class="form-range me-3"
                      :min="1"
                      :max="criterion.max_score || 5"
                      step="1"
                      v-model.number="criteriaResponses[criterion.id].rating"
                      :required="!criterion.is_optional"
                    >
                    <span class="badge bg-primary" style="min-width: 2.5rem;">
                      {{ criteriaResponses[criterion.id].rating || 1 }}
                    </span>
                  </div>
                  <div class="d-flex justify-content-between small text-muted">
                    <span>1 - {{ getRatingLabel(1, criterion) }}</span>
                    <span>{{ Math.ceil((criterion.max_score || 5) / 2) }} - {{ getRatingLabel(Math.ceil((criterion.max_score || 5) / 2), criterion) }}</span>
                    <span>{{ criterion.max_score || 5 }} - {{ getRatingLabel(criterion.max_score || 5, criterion) }}</span>
                  </div>
                </template>

                <!-- Boolean (Yes/No) -->
                <div v-else-if="criterion.answer_type === 'boolean'" class="btn-group w-100" role="group">
                  <input
                    type="radio"
                    class="btn-check"
                    :id="`${criterion.id}-yes`"
                    :value="true"
                    v-model="criteriaResponses[criterion.id].booleanResponse"
                    :required="!criterion.is_optional"
                  >
                  <label class="btn btn-outline-primary" :for="`${criterion.id}-yes`">
                    <i class="icon-feather-check"></i> Yes
                  </label>

                  <input
                    type="radio"
                    class="btn-check"
                    :id="`${criterion.id}-no`"
                    :value="false"
                    v-model="criteriaResponses[criterion.id].booleanResponse"
                  >
                  <label class="btn btn-outline-danger" :for="`${criterion.id}-no`">
                    <i class="icon-feather-x"></i> No
                  </label>
                </div>

                <!-- Multiple Choice (Checkboxes) -->
                <div v-else-if="criterion.answer_type === 'multiple_choice' && criterion.options?.length" class="d-flex flex-column gap-2">
                  <div v-for="(option, optIndex) in criterion.options" :key="optIndex" class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :value="option"
                      :id="`${criterion.id}-opt-${optIndex}`"
                      :checked="criteriaResponses[criterion.id]?.selectedOptions?.includes(option)"
                      @change="handleMultipleChoiceChange($event, criterion.id, option)"
                      :required="!criterion.is_optional && !criteriaResponses[criterion.id]?.selectedOptions?.length"
                    >
                    <label class="form-check-label" :for="`${criterion.id}-opt-${optIndex}`">
                      {{ option }}
                    </label>
                  </div>
                </div>

                <!-- Single Select Dropdown -->
                <select
                  v-else-if="criterion.answer_type === 'select' && criterion.options?.length"
                  v-model="criteriaResponses[criterion.id].selectedOption"
                  class="form-select"
                  :required="!criterion.is_optional"
                >
                  <option :value="null" disabled>Select an option</option>
                  <option v-for="(option, optIndex) in criterion.options" :key="optIndex" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <!-- Additional comments -->
              <div class="mb-2">
                <label class="form-label">Additional Comments (Optional)</label>
                <textarea
                  v-model="criteriaResponses[criterion.id].comments"
                  class="form-control"
                  rows="2"
                  placeholder="Any additional context or comments..."
                ></textarea>
              </div>
            </div>

            <!-- Section-level comments -->
            <div class="mt-3">
              <label class="form-label">Overall Comments for {{ section.title }} (Optional)</label>
              <textarea
                v-model="sectionResponses[section.title].comments"
                class="form-control"
                rows="2"
                :placeholder="`Any additional comments about ${section.title}...`"
              ></textarea>
            </div>
          </div>
        </template>

        <!-- Goals for Next Period -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Goals for Next Period</h6>
          <textarea
            v-model="formData.goals_next_period"
            class="form-control"
            rows="4"
            placeholder="What are your goals for the next review period?"
          ></textarea>
        </div>

        <!-- Development Areas -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Development Areas</h6>
          <textarea
            v-model="formData.development_areas"
            class="form-control"
            rows="4"
            placeholder="What areas would you like to develop or improve?"
          ></textarea>
        </div>

        <!-- Submit Button -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary" @click="saveDraft" :disabled="!canSaveDraft">
            <i class="icon-feather-save me-2"></i>Save Draft
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="icon-feather-send me-2"></i>
            Submit Self Review
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {computed, ref, watch} from 'vue';
import notify from '@/utils/notify';

export default {
  name: 'SelfReviewForm',
  props: {
    review: {
      type: Object,
      required: true
    },
    canSubmit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submitted', 'saved'],
  setup(props, { emit }) {
    const isInitialized = ref(false);
    const submitting = ref(false);
    const sectionResponses = ref({});
    const formData = ref({
      goals_next_period: '',
      development_areas: ''
    });

    // Helper to safely get status message
    const getStatusMessage = () => {
      if (!props.review) return 'Review data is loading...';
      return `Self review is not available at this stage. Current status: ${formatStatus(props.review.status)}`;
    };

    // Handle multiple choice checkbox changes
    const handleMultipleChoiceChange = (event, criterionId, option) => {
      if (!criteriaResponses.value[criterionId]) {
        criteriaResponses.value[criterionId] = { selectedOptions: [] };
      }

      if (!criteriaResponses.value[criterionId].selectedOptions) {
        criteriaResponses.value[criterionId].selectedOptions = [];
      }

      const selectedOptions = [...(criteriaResponses.value[criterionId].selectedOptions || [])];

      if (event.target.checked) {
        // Add option if not already in the array
        if (!selectedOptions.includes(option)) {
          selectedOptions.push(option);
        }
      } else {
        // Remove option if it exists in the array
        const index = selectedOptions.indexOf(option);
        if (index > -1) {
          selectedOptions.splice(index, 1);
        }
      }

      // Update the reactive object
      criteriaResponses.value[criterionId].selectedOptions = selectedOptions;
    };

    // Get template sections from review_cycle.template.sections
    // Get template sections from review_cycle.template.sections
    const templateSections = computed(() => {
      return props.review?.review_cycle?.template?.sections || [];
    });

    // Get all criteria from the template
    const templateCriteria = computed(() => {
      return props.review?.review_cycle?.template?.criteria || [];
    });

    // Get criteria for a specific section
    const getCriteriaForSection = (sectionTitle) => {
      return templateCriteria.value.filter(c => c.section === sectionTitle)
                                 .sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // Helper to get rating labels
    const getRatingLabel = (rating, criterion) => {
      if (criterion.rating_labels && criterion.rating_labels[rating - 1]) {
        return criterion.rating_labels[rating - 1];
      }
      return rating === 1 ? 'Poor' : rating === 3 ? 'Average' : 'Excellent';
    };

    // Track criteria responses
    const criteriaResponses = ref({});

    // Initialize responses for each section and criteria
    const initializeResponses = () => {
      if (!templateSections.value.length) return;

      // Initialize section responses
      templateSections.value.forEach(section => {
        const existingResponse = props.review.self_review?.responses?.find(
          r => r.section_title === section.title
        );

        sectionResponses.value[section.title] = {
          section_title: section.title,
          comments: existingResponse?.comments || ''
        };

        // Initialize criteria responses for this section
        getCriteriaForSection(section.title).forEach(criterion => {
          const existingCriterionResponse = existingResponse?.criteria_responses?.find(
            cr => cr.criterion_id === criterion.id
          );

          // Initialize response object with proper default values based on answer type
          const response = {
            criterion_id: criterion.id,
            response: existingCriterionResponse?.response || '',
            comments: existingCriterionResponse?.comments || '',
            rating: existingCriterionResponse?.rating || 1,
            selectedOption: existingCriterionResponse?.selected_option || '',
            selectedOptions: Array.isArray(existingCriterionResponse?.selected_options)
              ? [...existingCriterionResponse.selected_options]
              : [],
            booleanResponse: existingCriterionResponse?.boolean_response ?? null
          };

          criteriaResponses.value[criterion.id] = response;
        });
      });

      // Initialize form data from existing review if available
      if (props.review.self_review) {
        formData.value = {
          goals_next_period: props.review.self_review.goals_next_period || '',
          development_areas: props.review.self_review.development_areas || ''
        };
      }
    };

    const prepareResponses = () => {
      const responses = [];

      templateSections.value.forEach(section => {
        getCriteriaForSection(section.title).forEach(criterion => {
          const response = criteriaResponses.value[criterion.id] || {};

          // Format response based on answer type
          let responseValue = '';
          let score = null;

          switch (criterion.answer_type) {
            case 'rating':
              responseValue = response.rating?.toString() || '';
              score = response.rating || null;
              break;
            case 'select':
              responseValue = response.selectedOption || '';
              break;
            case 'multiple_choice':
              responseValue = Array.isArray(response.selectedOptions)
                ? response.selectedOptions.join('|')
                : '';
              break;
            case 'boolean':
              responseValue = response.booleanResponse !== null
                ? response.booleanResponse.toString()
                : '';
              break;
            default: // text and other types
              responseValue = response.response || '';
          }

          responses.push({
            criteria_id: criterion.id,
            response: responseValue,
            score: score,
            comments: response.comments || '',
            section_title: section.title
          });
        });
      });

      return responses;
    };

    const submitReview = async () => {
      submitting.value = true;
      try {
        const responses = prepareResponses();

        const reviewData = {
          responses: responses,
          goals_next_period: formData.value.goals_next_period || null,
          development_areas: formData.value.development_areas || null
        };

        emit('submitted', reviewData);
      } catch (error) {
        console.error('Error submitting review:', error);
        notify.alert.error('Failed to submit review. Please try again.');
      } finally {
        submitting.value = false;
      }
    };

    // Get a unique key for localStorage based on review ID
    const getDraftKey = () => `review_draft_${props.review.id}`;

    // Load draft from localStorage
    const loadDraft = () => {
      const draftKey = getDraftKey();
      const savedDraft = localStorage.getItem(draftKey);

      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);

          // Update form data
          if (draft.goals_next_period) {
            formData.value.goals_next_period = draft.goals_next_period;
          }

          if (draft.development_areas) {
            formData.value.development_areas = draft.development_areas;
          }

          // Update criteria responses
          if (Array.isArray(draft.responses)) {
            draft.responses.forEach(response => {
              if (criteriaResponses.value[response.criteria_id]) {
                criteriaResponses.value[response.criteria_id] = {
                  ...criteriaResponses.value[response.criteria_id],
                  response: response.response || '',
                  comments: response.comments || '',
                  rating: response.score || 1,
                  selectedOption: response.response || '',
                  selectedOptions: response.response ? response.response.split('|') : []
                };
              }
            });
          }

          notify.alert.info('Draft loaded successfully');
        } catch (error) {
          console.error('Error loading draft:', error);
          notify.alert.error('Failed to load draft');
        }
      }
    };

    // Save draft to localStorage
    const saveDraft = () => {
      try {
        const responses = prepareResponses();
        const draftKey = getDraftKey();

        const draftData = {
          responses: responses,
          goals_next_period: formData.value.goals_next_period || '',
          development_areas: formData.value.development_areas || '',
          saved_at: new Date().toISOString()
        };

        localStorage.setItem(draftKey, JSON.stringify(draftData));
        notify.alert.success('Draft saved locally');
      } catch (error) {
        console.error('Error saving draft:', error);
        notify.alert.error('Failed to save draft. Please try again.');
      }
    };

    const formatStatus = (status) => {
      const statusMap = {
        'draft': 'Draft',
        'self_review': 'Self Review',
        'manager_review': 'Manager Review',
        'calibration': 'Calibration',
        'completed': 'Completed',
        'acknowledged': 'Acknowledged'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        draft: 'badge bg-secondary',
        pending: 'badge bg-secondary',
        self_review: 'badge bg-warning',
        manager_review: 'badge bg-info',
        calibration: 'badge bg-primary',
        completed: 'badge bg-success',
        acknowledged: 'badge bg-success'
      };
      return classes[status] || 'badge bg-secondary';
    };

    const isCompleted = computed(() =>
      props.review.status === 'manager_review' ||
      props.review.status === 'calibration' ||
      props.review.status === 'completed' ||
      props.review.self_reviewed_at !== null
    );

    // Less strict validation for saving drafts
    const canSaveDraft = computed(() => {
      if (!templateSections.value.length) return false;

      // For draft, just check if there's at least one response
      return Object.keys(criteriaResponses.value).length > 0 ||
             formData.value.goals_next_period.trim() !== '' ||
             formData.value.development_areas.trim() !== '';
    });

    // Stricter validation for final submission
    const canSubmit = computed(() => {
      if (!templateSections.value.length) return false;

      // Check if all required fields are filled
      return templateSections.value.every(section => {
        const sectionCriteria = getCriteriaForSection(section.title);
        return sectionCriteria.every(criterion => {
          const response = criteriaResponses.value[criterion.id];
          if (!response) return false;

          // Check response based on answer type
          if (criterion.answer_type === 'rating') {
            return response.rating >= 1 && response.rating <= (criterion.max_score || 5);
          } else if (criterion.answer_type === 'select') {
            return !!response.selectedOption;
          } else if (criterion.answer_type === 'multiple_choice') {
            return response.selectedOptions && response.selectedOptions.length > 0;
          } else if (criterion.answer_type === 'boolean') {
            return response.booleanResponse !== null;
          } else {
            // For text or other types
            return response.response?.trim() !== '';
          }
        });
      });
    });

    // Watch for changes in template sections to initialize responses
    watch(() => props.review?.review_cycle?.template, (newVal) => {
      if (newVal) {
        initializeResponses();
        // Load draft after initializing responses
        loadDraft();
        isInitialized.value = true;
      }
    }, { immediate: true, deep: true });

    // Clear draft after successful submission
    watch(() => props.review?.status, (newStatus) => {
      if (newStatus === 'submitted' || newStatus === 'manager_review') {
        const draftKey = getDraftKey();
        localStorage.removeItem(draftKey);
      }
    });

    // For displaying submitted reviews
    const categorizedResponses = computed(() => {
      if (!props.review?.responses) return [];

      const sections = {};
      // Handle both old and new response formats
      const responses = Array.isArray(props.review.responses)
        ? props.review.responses
        : [];

      responses.forEach(response => {
        const sectionName = response.section_title || response.criteria?.section || 'General';
        if (!sections[sectionName]) {
          sections[sectionName] = {
            name: sectionName,
            responses: []
          };
        }
        sections[sectionName].responses.push(response);
      });

      return Object.values(sections);
    });

    return {
      isInitialized,
      getStatusMessage,
      submitting,
      formData,
      sectionResponses,
      templateSections,
      criteriaResponses,
      submitReview,
      saveDraft,
      formatStatus,
      isCompleted,
      canSaveDraft,
      categorizedResponses,
      getCriteriaForSection,
      getRatingLabel,
      handleMultipleChoiceChange,
      getStatusBadgeClass,
      initializeResponses: () => {
        if (!templateSections.value.length) return;

        // Initialize section responses
        templateSections.value.forEach(section => {
          const existingResponse = props.review.self_review?.responses?.find(
            r => r.section_title === section.title
          );

          sectionResponses.value[section.title] = {
            section_title: section.title,
            comments: existingResponse?.comments || ''
          };

          // Initialize criteria responses for this section
          getCriteriaForSection(section.title).forEach(criterion => {
            const existingCriterionResponse = existingResponse?.criteria_responses?.find(
              cr => cr.criterion_id === criterion.id
            );

            criteriaResponses.value[criterion.id] = {
              criterion_id: criterion.id,
              response: existingCriterionResponse?.response || '',
              comments: existingCriterionResponse?.comments || '',
              rating: existingCriterionResponse?.rating || 1,
              selectedOption: existingCriterionResponse?.selected_option || null,
              selectedOptions: existingCriterionResponse?.selected_options || [],
              booleanResponse: existingCriterionResponse?.boolean_response ?? null
            };
          });
        });

        if (props.review.self_review) {
          formData.value = {
            goals_next_period: props.review.self_review.goals_next_period || '',
            development_areas: props.review.self_review.development_areas || ''
          };
        }
      }
    };
  },
};
</script>

<style scoped>
.form-range {
  width: 200px;
}

/* Modern header/banner helpers */
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.08);
}
.soft-gradient {
  background: linear-gradient(135deg, rgba(101,42,163,0.06) 0%, rgba(13,110,253,0.04) 100%);
  border-radius: 0.5rem;
}
</style>
